// packages/activity/src/lib/character-edits.ts
export type AspectKind = "character" | "situation" | "boost" | "scene" | "zone" | "setting";
export type AspectVisibility = "revealed" | "hidden";
export type AspectLifetime = "persistent" | "scene" | "exchange" | "single-use";
export type AspectScope = "table" | "scene" | "zone" | "character" | "npc";

export interface AspectRecord {
  id: string;
  text: string;
  kind: AspectKind;
  visibility: AspectVisibility;
  lifetime: AspectLifetime;
  scope: AspectScope;
  createdAt?: number;
}

export interface StuntRecord {
  id: string;
  nameKey: string;
  descKey: string;
  cost: {
    type: "passive" | "fp" | "per_scene" | "per_session" | "conditional";
    amount?: number;
    limit?: number;
    conditionText?: string;
  };
}

export interface ExtraRecord {
  id: string;
  name: string;
  kind: "companion" | "gear" | "vehicle" | "power" | "other";
  description?: string;
  notes?: string;
  counters?: Array<{
    id: string;
    labelKey: string;
    current: number;
    max: number;
    gmOnly?: boolean;
  }>;
}

export interface EditableCharacter {
  uid: string;
  name: string;
  aspects?: AspectRecord[];
  skills?: Record<string, number>;
  stunts?: StuntRecord[];
  refresh: number;
  fp: number;
  stressState?: Record<string, boolean[]>;
  consequences?: Record<string, { aspect: AspectRecord; filled: boolean }>;
  extras?: Record<string, ExtraRecord>;
}

export type WriteMap = Record<string, unknown>;

export interface IdClock {
  now?: number;
  idSeed?: string;
}

function genId(kind: string, clock?: IdClock): string {
  if (clock?.idSeed) return clock.idSeed;
  return `${kind}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
}

function now(clock?: IdClock): number {
  return clock?.now ?? Date.now();
}

export interface NewAspectInput {
  text: string;
  kind: AspectKind;
  lifetime: AspectLifetime;
  scope: AspectScope;
  visibility?: AspectVisibility;
}

export function addAspect(
  path: string,
  character: EditableCharacter,
  input: NewAspectInput,
  clock?: IdClock,
): WriteMap {
  const newAspect: AspectRecord = {
    id: genId(input.kind, clock),
    text: input.text,
    kind: input.kind,
    visibility: input.visibility ?? "revealed",
    lifetime: input.lifetime,
    scope: input.scope,
    createdAt: now(clock),
  };
  const aspects = [...(character.aspects ?? []), newAspect];
  return { [`${path}/aspects`]: aspects };
}

export function removeAspect(path: string, character: EditableCharacter, id: string): WriteMap {
  const aspects = (character.aspects ?? []).filter((a) => a.id !== id);
  return { [`${path}/aspects`]: aspects };
}

export function updateAspect(
  path: string,
  character: EditableCharacter,
  id: string,
  patch: Partial<Omit<AspectRecord, "id">>,
): WriteMap {
  const aspects = (character.aspects ?? []).map((a) => (a.id === id ? { ...a, ...patch } : a));
  return { [`${path}/aspects`]: aspects };
}

function clamp(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, n));
}

export function setSkillRank(
  path: string,
  _character: EditableCharacter,
  skillId: string,
  rank: number,
): WriteMap {
  return { [`${path}/skills/${skillId}`]: clamp(rank, -2, 10) };
}

export function removeSkill(path: string, skillId: string): WriteMap {
  return { [`${path}/skills/${skillId}`]: null };
}

export function adjustFp(path: string, character: EditableCharacter, delta: number): WriteMap {
  const cap = Math.max(character.refresh * 3, character.refresh);
  const next = clamp(character.fp + delta, 0, cap);
  return { [`${path}/fp`]: next };
}

export function toggleStressBox(
  path: string,
  character: EditableCharacter,
  trackId: string,
  boxIndex: number,
): WriteMap {
  const boxes = character.stressState?.[trackId] ?? [];
  const currentVal = boxes[boxIndex] ?? false;
  return { [`${path}/stressState/${trackId}/${boxIndex}`]: !currentVal };
}

export function setConsequence(
  path: string,
  slotId: string,
  input: { text: string; kind?: AspectKind; scope?: AspectScope; lifetime?: AspectLifetime },
  clock?: IdClock,
): WriteMap {
  const aspect: AspectRecord = {
    id: genId(slotId, clock),
    text: input.text,
    kind: input.kind ?? "character",
    visibility: "revealed",
    lifetime: input.lifetime ?? "persistent",
    scope: input.scope ?? "character",
    createdAt: now(clock),
  };
  return { [`${path}/consequences/${slotId}`]: { aspect, filled: true } };
}

export function clearConsequence(path: string, slotId: string): WriteMap {
  return { [`${path}/consequences/${slotId}`]: null };
}

export function addCustomStunt(
  path: string,
  character: EditableCharacter,
  stunt: Omit<StuntRecord, "id">,
  clock?: IdClock,
): WriteMap {
  const withId: StuntRecord = { id: genId("stunt", clock), ...stunt };
  const stunts = [...(character.stunts ?? []), withId];
  return { [`${path}/stunts`]: stunts };
}

export function removeStunt(path: string, character: EditableCharacter, id: string): WriteMap {
  const stunts = (character.stunts ?? []).filter((s) => s.id !== id);
  return { [`${path}/stunts`]: stunts };
}

export function setExtra(path: string, extra: ExtraRecord): WriteMap {
  return { [`${path}/extras/${extra.id}`]: extra };
}

export function removeExtra(path: string, id: string): WriteMap {
  return { [`${path}/extras/${id}`]: null };
}
